/**
 * Koerperkarte mit umschaltbaren Ansichten (F-34 C2/C3): Trainingsvolumen je
 * Muskel im Vergleich zu Oles Zielen, Regeneration (Schaetzung), Trainingsstand
 * (Raenge) und die heutige Einheit. Uebersicht (18 Gruppen) oder Detail
 * (35 Muskeln), Legende daneben, Werte als Text beim Antippen.
 */
import { useMemo, useState } from 'react';
import { useLiveQuery } from 'dexie-react-hooks';

import { db } from '@/db/schema';
import { einstellungenAendern } from '@/db/repositories/training';
import { beitragJeMuskel, GRUPPEN, MUSKELN, type GruppeId, type MuskelId } from '@/domain/training/muskeln';
import { status, STATUS_TEXT, volumen, volumenFuer, zielVon, type MuskelStatus, type Volumen, type Zeitraum } from '@/domain/training/muskelcheck';
import { ERHOLUNG_TEXT, erholung, erholungDerGruppe, type ErholungsStufe } from '@/domain/training/regeneration';
import { ausdauerRang, gesamtRang, RANG_TEXT, RAENGE, rangVerlauf, type RangErgebnis } from '@/domain/training/rang';
import type { KartenAnsicht, PlanEinheit, TrainingsEinstellungen, TrainingsProtokoll, Uebung } from '@/domain/training/typen';
import { Koerperbild } from '@/modules/training/koerper/Koerperbild';
import { ERHOLUNG_FARBE, RANG_FARBE, STATUS_FARBE } from '@/modules/training/koerper/farben';
import { Gesamtraenge, RangDetails, RegenerationDetails, RegenerationsEinstellung, VolumenDetails } from '@/modules/training/koerper/Panels';
import { erholtAbText, gruppenMuskeln, useJetzt } from '@/modules/training/koerper/hilfen';
import { zahl1 } from '@/modules/training/format';
import { cn } from '@/lib/utils/cn';
import type { LocalDate } from '@/domain/types';

const ZEITRAUM: Record<Zeitraum, string> = { woche: 'Diese Woche', vorwoche: 'Letzte Woche', vier: 'Ø 4 Wochen' };
const ANSICHTEN: { key: KartenAnsicht; label: string; frage: string }[] = [
  { key: 'volumen', label: 'Trainingsvolumen', frage: 'Wie gut wurde jeder Muskel trainiert – gemessen an meinen Zielen?' },
  { key: 'regeneration', label: 'Regeneration', frage: 'Welche Muskeln sind laut Schätzung schon wieder erholt?' },
  { key: 'stand', label: 'Trainingsstand', frage: 'Wo stehe ich je Muskelgruppe – Kraft, Beständigkeit und Entwicklung?' },
  { key: 'heute', label: 'Heutige Einheit', frage: 'Welche Muskeln trifft die heutige Einheit?' },
];
const HAUPT = 'var(--color-accent)';
const HILF = 'color-mix(in oklch, var(--color-accent) 45%, var(--color-koerper))';

/** Belastung der heutigen Einheit: tatsaechlich, wenn schon trainiert, sonst laut Plan. */
function heuteBelastung(protokolle: TrainingsProtokoll[], geplant: PlanEinheit[], bibliothek: ReadonlyMap<string, Uebung>, heute: LocalDate): { werte: Map<MuskelId, number>; quelle: 'ist' | 'plan' | null } {
  const ist = volumen(protokolle, bibliothek, heute, heute);
  const getan = Object.entries(ist.muskeln).filter(([, w]) => w.saetze > 0);
  if (getan.length) return { werte: new Map(getan.map(([m, w]) => [m as MuskelId, w.saetze])), quelle: 'ist' };
  const m = new Map<MuskelId, number>();
  for (const e of geplant) for (const pu of e.uebungen) {
    const u = bibliothek.get(pu.uebungId);
    if (!u || u.typ === 'mobility') continue;
    const n = pu.saetze.filter((s) => s.art !== 'aufwaermen').length;
    for (const [k, w] of beitragJeMuskel(u.hauptmuskeln, u.hilfsmuskeln)) m.set(k, (m.get(k) ?? 0) + w * n);
  }
  return { werte: m, quelle: m.size ? 'plan' : null };
}

export function MuskelKarte({ protokolle, bibliothek, heute, einstellungen, geplantHeute, raenge, koerpergewicht }: {
  protokolle: TrainingsProtokoll[];
  bibliothek: ReadonlyMap<string, Uebung>;
  heute: LocalDate;
  einstellungen?: TrainingsEinstellungen;
  geplantHeute: PlanEinheit[];
  raenge: RangErgebnis[];
  koerpergewicht: number | null;
}) {
  const ansicht: KartenAnsicht = einstellungen?.kartenAnsicht ?? 'volumen';
  const [zeitraum, setZeitraum] = useState<Zeitraum>('woche');
  const [detailWahl, setDetail] = useState(false);
  const [gewaehlt, setGewaehlt] = useState<MuskelId | null>(null);
  // Raenge gibt es nur je Gruppe.
  const detail = detailWahl && ansicht !== 'stand';
  const ziele = einstellungen?.muskelZiele;

  const muedeListe = useLiveQuery(() => db.muskelgefuehl.where('datum').equals(heute).toArray(), [heute], []);
  const muede = useMemo(() => new Set(muedeListe.map((x) => x.muskel)), [muedeListe]);
  const v: Volumen = useMemo(() => volumenFuer(protokolle, bibliothek, heute, zeitraum), [protokolle, bibliothek, heute, zeitraum]);
  const h = useMemo(() => heuteBelastung(protokolle, geplantHeute, bibliothek, heute), [protokolle, geplantHeute, bibliothek, heute]);
  const jetzt = useJetzt();
  const erh = useMemo(() => erholung(protokolle, bibliothek, jetzt, einstellungen?.regeneration, muede), [protokolle, bibliothek, jetzt, einstellungen?.regeneration, muede]);
  const rangJe = useMemo(() => new Map(raenge.map((r) => [r.gruppe, r])), [raenge]);
  const gesamt = useMemo(() => gesamtRang(raenge), [raenge]);
  const ausdauer = useMemo(() => ausdauerRang(protokolle, heute), [protokolle, heute]);
  const gewaehlteGruppe = gewaehlt ? MUSKELN[gewaehlt].gruppe : null;
  const verlauf = useMemo(
    () => (ansicht === 'stand' && gewaehlteGruppe
      ? rangVerlauf({ protokolle, bibliothek, heute, koerpergewicht, ziele, standards: einstellungen?.kraftStandards }, gewaehlteGruppe)
      : []),
    [ansicht, gewaehlteGruppe, protokolle, bibliothek, heute, koerpergewicht, ziele, einstellungen?.kraftStandards],
  );

  const gruppeVon = (m: MuskelId): GruppeId => MUSKELN[m].gruppe;
  const name = (m: MuskelId) => (detail ? MUSKELN[m].label : GRUPPEN[gruppeVon(m)].label);
  const wert = (m: MuskelId) => (detail ? v.muskeln[m] : v.gruppen[gruppeVon(m)]);
  const st = (m: MuskelId) => status(wert(m), zielVon(ziele, gruppeVon(m)));
  const heuteWert = (m: MuskelId) => (detail ? h.werte.get(m) ?? 0 : Math.max(0, ...[...h.werte].filter(([k]) => gruppeVon(k) === gruppeVon(m)).map(([, x]) => x)));
  const erhMuskeln = (m: MuskelId) => (detail ? [m] : gruppenMuskeln(gruppeVon(m)));
  const e = (m: MuskelId) => erholungDerGruppe(erh, erhMuskeln(m));
  const rang = (m: MuskelId) => rangJe.get(gruppeVon(m));

  const farbe: Record<KartenAnsicht, (m: MuskelId) => string> = {
    volumen: (m) => STATUS_FARBE[st(m)],
    regeneration: (m) => ERHOLUNG_FARBE[e(m).stufe],
    stand: (m) => { const r = rang(m)?.rang; return r ? RANG_FARBE[r] : STATUS_FARBE.nicht; },
    heute: (m) => { const x = heuteWert(m); const haupt = [...h.werte].some(([k, n]) => (detail ? k === m : gruppeVon(k) === gruppeVon(m)) && n >= 1); return x <= 0 ? STATUS_FARBE.nicht : haupt ? HAUPT : HILF; },
  };
  const label: Record<KartenAnsicht, (m: MuskelId) => string> = {
    volumen: (m) => `${name(m)}: ${zahl1(wert(m).saetze)} Sätze, ${zahl1(wert(m).tage)} Tage – ${STATUS_TEXT[st(m)]}`,
    regeneration: (m) => `${name(m)}: ${e(m).prozent} % erholt${e(m).stufe === 'erholt' ? '' : `, ${erholtAbText(e(m).erholtAb)}`}`,
    stand: (m) => { const r = rang(m); return `${name(m)}: ${r?.rang ? `${RANG_TEXT[r.rang]} (${zahl1(r.wert!)})` : 'noch nicht eingestuft'}`; },
    heute: (m) => `${name(m)}: ${heuteWert(m) > 0 ? `${zahl1(heuteWert(m))} Sätze heute` : 'heute nicht belastet'}`,
  };
  const legende = ({
    volumen: (Object.keys(STATUS_TEXT) as MuskelStatus[]).map((s): [string, string] => [STATUS_TEXT[s], STATUS_FARBE[s]]),
    regeneration: (Object.keys(ERHOLUNG_TEXT) as ErholungsStufe[]).map((s): [string, string] => [ERHOLUNG_TEXT[s], ERHOLUNG_FARBE[s]]),
    stand: [...RAENGE.map((r): [string, string] => [RANG_TEXT[r], RANG_FARBE[r]]), ['nicht eingestuft', STATUS_FARBE.nicht]],
    heute: [['Hauptmuskel', HAUPT], ['nur Hilfsmuskel', HILF], ['nicht belastet', STATUS_FARBE.nicht]],
  } satisfies Record<KartenAnsicht, [string, string][]>)[ansicht];

  return (
    <section aria-label="Körperkarte" className="flex flex-col gap-3 rounded-lg border border-line bg-surface px-4 py-3.5">
      <div>
        <h2 className="text-sm font-semibold">Körperkarte</h2>
        <p className="text-[12px] text-ink-muted">{ANSICHTEN.find((a) => a.key === ansicht)!.frage}</p>
      </div>
      <div role="tablist" aria-label="Ansicht" className="flex flex-wrap gap-1.5">
        {ANSICHTEN.map((a) => (
          <button key={a.key} type="button" role="tab" aria-selected={ansicht === a.key} onClick={() => void einstellungenAendern({ kartenAnsicht: a.key })}
            className={cn('rounded-full border px-3 py-1 text-[12px]', ansicht === a.key ? 'border-accent bg-accent/10 text-ink' : 'border-line text-ink-muted hover:text-ink')}>{a.label}</button>
        ))}
      </div>
      <div className="flex flex-wrap items-center gap-2 text-[12px]">
        {ansicht === 'volumen' && (
          <select value={zeitraum} onChange={(ev) => setZeitraum(ev.target.value as Zeitraum)} aria-label="Zeitraum"
            className="h-8 rounded-md border border-line bg-surface px-2 text-ink focus:border-accent focus:outline-none">
            {(Object.keys(ZEITRAUM) as Zeitraum[]).map((z) => <option key={z} value={z}>{ZEITRAUM[z]}</option>)}
          </select>
        )}
        {ansicht !== 'stand' && (
          <div role="group" aria-label="Detailstufe" className="flex overflow-hidden rounded-md border border-line">
            {[false, true].map((d) => (
              <button key={String(d)} type="button" aria-pressed={detail === d} onClick={() => { setDetail(d); setGewaehlt(null); }}
                className={cn('px-2.5 py-1', detail === d ? 'bg-surface-raised font-medium text-ink' : 'text-ink-muted')}>{d ? 'Detail (35)' : 'Übersicht (18)'}</button>
            ))}
          </div>
        )}
      </div>

      {ansicht === 'stand' && <Gesamtraenge gesamt={gesamt} ausdauer={ausdauer} />}

      <Koerperbild farbe={farbe[ansicht]} label={label[ansicht]} detail={detail} gewaehlt={gewaehlt ? (detail ? gewaehlt : gruppeVon(gewaehlt)) : null} onWahl={setGewaehlt} />

      <ul aria-label="Legende" className="flex flex-wrap gap-x-3 gap-y-1 text-[11px] text-ink-muted">
        {legende.map(([t, f]) => <li key={t} className="flex items-center gap-1"><span className="size-3 rounded-sm" style={{ backgroundColor: f }} />{t}</li>)}
      </ul>

      {ansicht === 'volumen' && gewaehlt && <VolumenDetails name={name(gewaehlt)} w={wert(gewaehlt)} ziel={zielVon(ziele, gruppeVon(gewaehlt))} st={st(gewaehlt)} bibliothek={bibliothek} />}
      {ansicht === 'regeneration' && gewaehlt && <RegenerationDetails name={name(gewaehlt)} e={e(gewaehlt)} muskeln={erhMuskeln(gewaehlt)} muede={muede} />}
      {ansicht === 'stand' && gewaehlt && rang(gewaehlt) && <RangDetails r={rang(gewaehlt)!} name={GRUPPEN[gruppeVon(gewaehlt)].label} verlauf={verlauf} />}
      {ansicht === 'heute' && <p className="text-[12px] text-ink-muted">{h.quelle === 'ist' ? 'Tatsächlich belastet laut heutigem Protokoll.' : h.quelle === 'plan' ? 'Laut Plan – nach dem Training zeigt die Karte, was tatsächlich belastet wurde.' : 'Heute steht keine Kraft- oder Skill-Einheit an.'}{gewaehlt ? ` ${label.heute(gewaehlt)}.` : ''}</p>}

      {ansicht === 'regeneration' && (
        <>
          <p className="text-[12px] text-ink-muted">Schätzung, keine Messung: Schlaf, Ernährung und Stress wirken individuell. Das eigene Körpergefühl hat Vorrang – „Muskelkater“ antippen überschreibt die Schätzung für heute. Keine medizinische Aussage.</p>
          <RegenerationsEinstellung werte={einstellungen?.regeneration} />
        </>
      )}
      {ansicht === 'stand' && (
        <p className="text-[12px] text-ink-muted">
          Orientierungsmaß, kein Wettkampfwert: Kraft (50 %) aus dem geschätzten 1RM im Verhältnis zum Körpergewicht bzw. Wiederholungen oder Skill-Stufe, Beständigkeit (30 %) aus den Wochen im Ziel, Entwicklung (20 %) aus den letzten 4 Wochen.
          {koerpergewicht ? ` Körpergewicht: ${zahl1(koerpergewicht)} kg.` : ' Ohne Körpergewicht fehlt der Kraftteil bei Gewichtsübungen – unten eintragen.'} Eingestuft wird erst ab 4 Wochen Daten.
        </p>
      )}
      <p className="text-[11px] text-ink-faint">
        {detail ? 'Detailstufe: eine Näherung – wie stark ein Muskel arbeitet, hängt von Ausführung und Winkel ab. Die Übersicht ist verlässlicher. ' : ''}
        Hauptmuskel zählt voll, Hilfsmuskel halb. Ausdauer zählt hier nicht{ansicht === 'stand' ? ' – sie hat einen eigenen Rang' : ''}.
      </p>
    </section>
  );
}
